import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const BlogPage = () => {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then(({ data }) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
    document.title = lang === "en" ? "Blog – Fondation Cœur Solidaire" : "Actualités – Fondation Cœur Solidaire";
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="blog-page">
      <Seo
        title={t.seo.blog.title}
        description={t.seo.blog.description}
        path={lang === "en" ? "/news" : "/actualites"}
        lang={lang}
      />
      <Navbar />

      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors" data-testid="blog-back-home">
            <ArrowLeft size={14} /> {t.blogPage.back}
          </Link>
          <span className="eyebrow text-[#E8C77E]" data-testid="blog-eyebrow">{t.blogPage.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1]" data-testid="blog-title">
            {t.blogPage.title}
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            {t.blogPage.intro}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-narrow">
          {loading ? (
            <div className="text-center text-[#64748B] py-12">{t.blogPage.loading}</div>
          ) : posts.length === 0 ? (
            <div className="text-center text-[#64748B] py-12">{t.blogPage.empty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_40px_-20px_rgba(0,43,73,0.15)] flex flex-col hover:-translate-y-1 transition-transform"
                  data-testid={`blog-card-${i}`}
                >
                  <Link to={`/actualites/${p.slug}`} className="block aspect-[16/10] overflow-hidden bg-[#F3F0EA]">
                    {p.image_url && (
                      <img
                        src={p.image_url}
                        alt={lang === "en" ? p.title_en : p.title_fr}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </Link>
                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-xs text-[#64748B] mb-3">
                      <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {new Date(p.created_at).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", { year: "numeric", month: "short", day: "numeric" })}</span>
                      {p.category && <span className="inline-flex items-center gap-1.5 text-[#C5A059] font-medium uppercase tracking-wider"><Tag size={11} /> {p.category}</span>}
                    </div>
                    <Link to={`/actualites/${p.slug}`}>
                      <h2 className="font-serif text-xl md:text-2xl text-[#002B49] font-semibold leading-tight group-hover:text-[#C5A059] transition-colors">
                        {lang === "en" ? p.title_en : p.title_fr}
                      </h2>
                    </Link>
                    <p className="text-[#334155] text-sm leading-relaxed mt-3 flex-1">
                      {lang === "en" ? p.excerpt_en : p.excerpt_fr}
                    </p>
                    <Link
                      to={`/actualites/${p.slug}`}
                      className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-[#002B49] hover:text-[#C5A059] transition-colors"
                      data-testid={`blog-read-more-${i}`}
                    >
                      {t.blogPage.readMore}
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
